import { useEffect, useState, useCallback } from "react";
import { MessageSquare, Instagram, CalendarDays } from "lucide-react";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannelMulti } from "@/lib/realtime-channel";
import { OFFLINE_CONNECTION_STATES } from "@/lib/uazapi-connection-state";
import { isUazapiEnabledForWorkspace } from "@/lib/uazapi-feature-flags";

interface ChannelStatusIndicatorsProps {
  workspaceId: string | null;
}

type ChannelStatus = "active" | "pending" | "error" | "disconnected" | "none";

interface ChannelInfo {
  key: string;
  label: string;
  status: ChannelStatus;
  icon: React.ReactNode;
  dotColor: string;
}

const statusDotColor: Record<ChannelStatus, string> = {
  active: "bg-green-500",
  pending: "bg-yellow-500",
  error: "bg-red-500",
  disconnected: "bg-red-400",
  none: "bg-muted-foreground/40",
};

const statusLabel: Record<ChannelStatus, string> = {
  active: "Conectado",
  pending: "Pendente",
  error: "Erro",
  disconnected: "Desconectado",
  none: "Não conectado",
};

export function ChannelStatusIndicators({ workspaceId }: ChannelStatusIndicatorsProps) {
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const [workspaceRes, integrationsRes, calendarRes, planLimitsRes] = await Promise.all([
        supabase.from("workspaces").select("plan").eq("id", workspaceId).single(),
        supabase.from("integrations").select("provider, status, token_expires_at, config").eq("workspace_id", workspaceId),
        supabase
          .from("google_calendar_credentials")
          .select("is_active, needs_reconnect" as any)
          .eq("workspace_id", workspaceId),
        supabase.from("plan_limits").select("plan, max_whatsapp, max_instagram, max_integrations"),
      ]);

      const plan = (workspaceRes.data?.plan as string) || "inicial";
      const integrations = integrationsRes.data || [];
      const calendarCreds = calendarRes.data || [];
      const allLimits = planLimitsRes.data || [];

      const limits = allLimits.find((l) => l.plan === plan) || allLimits.find((l) => l.plan === "inicial");

      const whatsappConnectionState = (i: { config: unknown }) =>
        String((i.config as Record<string, unknown> | null)?.connection_state || "").toLowerCase();
      const isWhatsappConnecting = (i: { provider: string; config: unknown }) => {
        if (i.provider !== "whatsapp_evolution" && i.provider !== "whatsapp_uazapi") return false;
        const state = whatsappConnectionState(i);
        // "connecting" é transitório — não suspende disparos, mas o canal ainda não está operacional.
        return state === "connecting" || OFFLINE_CONNECTION_STATES.has(state);
      };

      const getIntegrationStatusFromMatches = (
        matches: typeof integrations,
      ): ChannelStatus => {
        if (matches.length === 0) return "none";

        const hasError = matches.some((i) => i.status === "error");
        const hasDisconnected = matches.some((i) => i.status === "disconnected" || isWhatsappConnecting(i));
        const hasActive = matches.some((i) => {
          if (i.status !== "active") return false;
          if (isWhatsappConnecting(i)) return false;
          const tokenExpiry = (i as { token_expires_at?: string | null }).token_expires_at;
          return !(tokenExpiry && new Date(tokenExpiry) < new Date());
        });
        const hasExpired = matches.some((i) => {
          if (i.status !== "active") return false;
          const tokenExpiry = (i as { token_expires_at?: string | null }).token_expires_at;
          return tokenExpiry && new Date(tokenExpiry) < new Date();
        });

        // O indicador resume o canal, não cada conexão. Se ao menos uma conexão
        // está operacional, o canal está disponível; alertas individuais continuam
        // aparecendo no badge de reconexão e na página de Integrações.
        if (hasActive) return "active";
        if (hasError || hasExpired) return "error";
        if (hasDisconnected) return "disconnected";
        if (matches.some((i) => i.status === "pending")) return "pending";
        return "disconnected";
      };

      const getIntegrationStatus = (provider: string): ChannelStatus =>
        getIntegrationStatusFromMatches(integrations.filter((i) => i.provider === provider));

      const getWhatsAppStatus = (): ChannelStatus => {
        const uazapiMatches = integrations.filter((i) => i.provider === "whatsapp_uazapi");
        const evolutionMatches = integrations.filter((i) => i.provider === "whatsapp_evolution");
        const uazapiAllowed = isUazapiEnabledForWorkspace(workspaceId);
        const relevantMatches =
          uazapiAllowed && uazapiMatches.length > 0 ? uazapiMatches : evolutionMatches;
        return getIntegrationStatusFromMatches(relevantMatches);
      };

      const result: ChannelInfo[] = [];

      if (limits && (limits.max_whatsapp > 0 || (limits.max_integrations && limits.max_integrations > 0))) {
        const status = getWhatsAppStatus();
        result.push({
          key: "whatsapp",
          label: "WhatsApp",
          status,
          icon: <MessageSquare className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />,
          dotColor: statusDotColor[status],
        });
      }

      if (limits && (limits.max_instagram > 0 || (limits.max_integrations && limits.max_integrations > 0))) {
        const status = getIntegrationStatus("instagram");
        result.push({
          key: "instagram",
          label: "Instagram",
          status,
          icon: <Instagram className="w-3.5 h-3.5 text-pink-500" />,
          dotColor: statusDotColor[status],
        });
      }

      if (limits && (limits.max_instagram > 0 || (limits.max_integrations && limits.max_integrations > 0))) {
        const status = getIntegrationStatus("messenger");
        result.push({
          key: "messenger",
          label: "Messenger",
          status,
          icon: <MessengerIcon className="text-blue-500" size={14} />,
          dotColor: statusDotColor[status],
        });
      }

      // Use needs_reconnect as source of truth for calendar status
      const activeCalendarCreds = calendarCreds.filter((c: any) => c.is_active);
      let calendarStatus: ChannelStatus;
      if (activeCalendarCreds.length === 0) {
        calendarStatus = calendarCreds.length === 0 ? "none" : "disconnected";
      } else {
        const anyNeedsReconnect = activeCalendarCreds.some((c: any) => c.needs_reconnect === true);
        calendarStatus = anyNeedsReconnect ? "error" : "active";
      }
      result.push({
        key: "calendar",
        label: "Calendar",
        status: calendarStatus,
        icon: <CalendarDays className="w-3.5 h-3.5 text-orange-500" />,
        dotColor: statusDotColor[calendarStatus],
      });

      setChannels(result);
    } catch (err) {
      console.error("Error fetching channel statuses:", err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;

    fetchStatuses();

    const channelName = `channel-status-${workspaceId}`;
    const channel = subscribePostgresChannelMulti(channelName, [
      {
        binding: { event: "*", schema: "public", table: "integrations", filter: `workspace_id=eq.${workspaceId}` },
        callback: fetchStatuses,
      },
      {
        binding: { event: "*", schema: "public", table: "google_calendar_credentials", filter: `workspace_id=eq.${workspaceId}` },
        callback: fetchStatuses,
      },
    ]);

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId, fetchStatuses]);

  if (loading || channels.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {channels.map((ch) => (
        <div
          key={ch.key}
          title={
            ch.key === "calendar" && ch.status === "error"
              ? `${ch.label}: token expirado — reconecte em Integrações`
              : `${ch.label}: ${statusLabel[ch.status]}`
          }
          className="flex items-center gap-1.5 px-2 py-1 h-7 rounded-full border border-border bg-card text-xs font-medium"
        >
          {ch.icon}
          <span className="hidden sm:inline text-muted-foreground">{ch.label}</span>
          <span className={`w-2 h-2 rounded-full ${ch.dotColor} shrink-0`} />
        </div>
      ))}
    </div>
  );
}
